import { TitleService } from '@hub-services/title.service';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppComponent } from './app.component';
import { RhCommonModule } from '@hub-common/rh-common.module';
import { environment } from '@hub-env/environment';

import { ErrorHandler } from '@angular/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
// import { AuthInterceptor } from './auth/auth.interceptor-old';

import { GlobalErrorHandler } from '@hub-services/global-error-handler';
import { AppRoutingModule, CustomPreload } from './app-routing.module';
import { HubUiModule } from '@atpco/hub-ui';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import * as fromChannels from '@hub-store/channel-usage/channel-usage.reducer';
import * as fromMedia from '@hub-store/media/media.reducer';
import * as fromPartner from '@hub-store/selected-partner/selected-partner.reducer';
import * as fromPartnersList from '@hub-store/partners-list/partners-list.reducer';
import * as fromSession from '@hub-store/session/session.reducer';
import * as fromHelp from '@hub-store/help/help.reducer';
import * as fromUser from '@hub-store/user/user.reducer';
import * as fromUaConfig from '@hub-store/ua-config/ua-config.reducer';
import * as fromMessages from '@hub-store/messages/messages.reducer';
import * as fromFilters from '@hub-store/filters/filters.reducer';
import * as fromSavedViews from '@hub-store/saved-views/saved-views.reducer';
import * as fromFlightSearch from '@hub-store/flight-search/flight-search.reducer';
import { EffectsModule } from '@ngrx/effects';
import { HubDefaultEffects } from '@hub-store/hub-store.effects';
import { MessagesEffects } from '@hub-store/messages/messages.effects';
import { FiltersEffects } from '@hub-store/filters/filters.effects';
import { AuthService } from './auth/auth.service';
import { DatePipe } from '@angular/common';
import { WINDOW, WINDOW_PROVIDERS } from './shared/window.providers';
import { NgxEchartsModule } from 'ngx-echarts';
import { AgGridModule } from 'ag-grid-angular';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MsalBroadcastService,
  MsalGuard,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import { AuthInterceptor } from './auth/auth.interceptor';
import { ApiTelemetryInterceptor } from './auth/api-telemetry.interceptor';
import { AuthGuard } from './auth/auth.guard';
import { initializeMsalApp, MSALGuardConfigFactory, MSALInstanceFactory } from './msal-config';
import { FeatureFlagsService } from '@hub-services/feature-flags.service';
import { RULES_DATA_PROVIDER } from '@hub-services/rules-data.provider';
import { FaroObservabilityService, initializeFaroObservability } from '@hub-services/faro-observability.service';
import { MsalTelemetryService, initializeMsalTelemetry } from './auth/msal-telemetry.service';

export const providers = [
  CustomPreload,
  // { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
  { provide: ErrorHandler, useClass: GlobalErrorHandler },
  {
    provide: APP_INITIALIZER,
    useFactory: initializeFaroObservability,
    deps: [FaroObservabilityService],
    multi: true,
  },
  // MsalTelemetry must subscribe to MsalBroadcastService.msalSubject$
  // BEFORE initializeMsalApp runs handleRedirectPromise(). Otherwise an
  // event emitted during MSAL bootstrap (e.g. LOGIN_FAILURE from a
  // returning redirect with an error fragment) fires before our
  // subscription is wired up and is lost. Order:
  //   1. Faro (telemetry sink must exist first)
  //   2. MsalTelemetry (subscribe to MSAL events)
  //   3. MsalApp (which then drives MSAL events)
  {
    provide: APP_INITIALIZER,
    useFactory: initializeMsalTelemetry,
    deps: [MsalTelemetryService],
    multi: true,
  },
  {
    provide: APP_INITIALIZER,
    useFactory: initializeMsalApp,
    deps: [MsalService, FeatureFlagsService, AuthService],
    multi: true,
  },
  {
    provide: MSAL_INSTANCE,
    useFactory: MSALInstanceFactory,
    deps: [WINDOW],
  },
  {
    provide: MSAL_GUARD_CONFIG,
    useFactory: MSALGuardConfigFactory,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: ApiTelemetryInterceptor,
    multi: true,
  },
  MsalService,
  MsalGuard,
  AuthGuard,
  MsalBroadcastService,
  DatePipe,
  ...WINDOW_PROVIDERS,
  RULES_DATA_PROVIDER,
];

@NgModule({
  declarations: [AppComponent],
  providers,
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    RhCommonModule.forRoot(),
    HubUiModule,
    AgGridModule,
    EffectsModule.forRoot([HubDefaultEffects, MessagesEffects, FiltersEffects]),
    StoreModule.forRoot({
      [fromChannels.featureKey]: fromChannels.reducer,
      [fromMedia.featureKey]: fromMedia.reducer,
      [fromPartner.featureKey]: fromPartner.reducer,
      [fromPartnersList.featureKey]: fromPartnersList.reducer,
      [fromSession.featureKey]: fromSession.reducer,
      [fromHelp.featureKey]: fromHelp.reducer,
      [fromUser.featureKey]: fromUser.reducer,
      [fromUaConfig.featureKey]: fromUaConfig.reducer,
      [fromMessages.featureKey]: fromMessages.reducer,
      [fromFilters.featureKey]: fromFilters.reducer,
      [fromSavedViews.featureKey]: fromSavedViews.reducer,
      [fromFlightSearch.featureKey]: fromFlightSearch.reducer,
    }, { metaReducers: [] }),
    !environment.production ? StoreDevtoolsModule.instrument({
      maxAge: 50,
      logOnly: true,
      name: 'Hub UI Store',
      connectInZone: true}) : [],
    NgxEchartsModule.forRoot({
      echarts: () => import('echarts'),
    }),
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {
  constructor(titleService: TitleService) {
    titleService.init();
  }
}
